/**
 * Shared datetime + interval helpers for editing user events (Tagger + Tags explore).
 */
import type { ExploreUserEventRow } from '@services/userEventsExploreService';

export function isoToDatetimeLocalValue(iso: string | null | undefined): string {
  if (!iso || iso.trim() === '') {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function datetimeLocalValueToIso(value: string): string | null {
  const v = value?.trim();
  if (!v) {
    return null;
  }
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return d.toISOString();
}

export function adjustDatetimeLocalBySeconds(value: string, seconds: number): string {
  const v = value?.trim() ?? '';
  if (v === '') {
    return '';
  }
  const iso = datetimeLocalValueToIso(v);
  if (iso == null) {
    return v;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return v;
  }
  d.setTime(d.getTime() + seconds * 1000);
  return isoToDatetimeLocalValue(d.toISOString());
}

export function localDateYmd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const TAGGER_FOCUS_TIME_EVENT_TYPES = new Set([
  'COMMENT',
  'JOB',
  'FLAG',
  'NICE',
  'GOOD_BALANCE',
  'BAD_MOMENT',
]);

export function taggerEventTypeUsesFocusTime(et: string): boolean {
  return TAGGER_FOCUS_TIME_EVENT_TYPES.has(et.trim().toUpperCase());
}

export function taggerEventTypeIsPrestart(et: string): boolean {
  return et.trim().toUpperCase() === 'PRESTART';
}

export function taggerEventTypeIsRace(et: string): boolean {
  return et.trim().toUpperCase() === 'RACE';
}

export function taggerEventTypeAllowsEmptyComment(et: string): boolean {
  const u = et.trim().toUpperCase();
  return u === 'PRESTART' || u === 'RACE';
}

/** Default comment when a TEST interval is started from the Tagger. */
export const TAGGER_TEST_DEFAULT_COMMENT = 'TEST';

/** Max length for a saved TEST comment (strictly less than 10). */
export const TAGGER_TEST_COMMENT_MAX_LENGTH = 9;

/**
 * Validate a TEST comment for save. Empty is already rejected by the generic comment rule;
 * this enforces the short length used when stamping tags.TEST onto dataset events.
 */
export function taggerValidateTestComment(comment: string): string | null {
  const text = comment.trim();
  if (text.length >= 10) {
    return 'Test comment must be less than 10 characters.';
  }
  return null;
}

/** Cap input while composing a TEST comment (max 9 characters). */
export function taggerClampTestCommentInput(value: string): string {
  return value.length <= TAGGER_TEST_COMMENT_MAX_LENGTH
    ? value
    : value.slice(0, TAGGER_TEST_COMMENT_MAX_LENGTH);
}

export function taggerUserEventShowsCommentField(et: string): boolean {
  const u = et.trim().toUpperCase();
  return u !== 'SAILS' && u !== 'CREW' && u !== 'CONFIG';
}

const TAGGER_PRESTART_START_OFFSET_MS = 2 * 60 * 1000;
const TAGGER_PRESTART_END_OFFSET_MS = 30 * 1000;
const TAGGER_PRESTART_LEGACY_WINDOW_MS =
  TAGGER_PRESTART_START_OFFSET_MS + TAGGER_PRESTART_END_OFFSET_MS;

type NewTagTimes = {
  focus_time: string;
  start_time: string | null;
  end_time: string | null;
  date: string;
};

function timestampsPointWindowFromPress(press: Date): NewTagTimes {
  const pressMs = press.getTime();
  const focusMs = pressMs - 5000;
  const startMs = focusMs - 5000;
  const endMs = focusMs + 5000;
  const focusDate = new Date(focusMs);
  return {
    focus_time: focusDate.toISOString(),
    start_time: new Date(startMs).toISOString(),
    end_time: new Date(endMs).toISOString(),
    date: localDateYmd(focusDate),
  };
}

function timestampsPrestartFromFocusTime(focus: Date): NewTagTimes {
  const focusMs = focus.getTime();
  const focusDate = new Date(focusMs);
  return {
    focus_time: focusDate.toISOString(),
    start_time: new Date(focusMs - TAGGER_PRESTART_START_OFFSET_MS).toISOString(),
    end_time: new Date(focusMs + TAGGER_PRESTART_END_OFFSET_MS).toISOString(),
    date: localDateYmd(focusDate),
  };
}

function isLegacyPrestartRow(row: ExploreUserEventRow): boolean {
  const start = row.start_time?.trim();
  const focus = row.focus_time?.trim();
  const end = row.end_time?.trim();
  if (!start || !focus || !end) {
    return false;
  }
  const startMs = new Date(start).getTime();
  const focusMs = new Date(focus).getTime();
  const endMs = new Date(end).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(focusMs) || Number.isNaN(endMs)) {
    return false;
  }
  return focusMs === endMs && endMs - startMs === TAGGER_PRESTART_LEGACY_WINDOW_MS;
}

export function prestartTimeIsoFromExploreRow(row: ExploreUserEventRow): string {
  const focus = row.focus_time?.trim();
  if (focus) {
    if (isLegacyPrestartRow(row)) {
      const start = row.start_time?.trim();
      if (start) {
        return start;
      }
    }
    return focus;
  }
  const start = row.start_time?.trim();
  if (start) {
    return start;
  }
  const end = row.end_time?.trim();
  if (!end) {
    return '';
  }
  const endMs = new Date(end).getTime();
  if (Number.isNaN(endMs)) {
    return '';
  }
  return new Date(endMs - TAGGER_PRESTART_END_OFFSET_MS).toISOString();
}

export function parseRaceNumberInput(raw: string): number | null {
  const t = raw.trim();
  if (t === '') {
    return null;
  }
  const n = Number(t);
  if (!Number.isFinite(n) || n < 1 || !Number.isInteger(n)) {
    return null;
  }
  return n;
}

export function raceNumberFromExploreTags(
  tags: Record<string, unknown> | null | undefined,
  et: string
): number | null {
  if (tags == null || typeof tags !== 'object') {
    return null;
  }
  const u = et.trim().toUpperCase();
  const raw =
    u === 'PRESTART'
      ? tags.Prestart_number ?? tags.prestart_number
      : tags.Race_number ?? tags.race_number;
  if (raw == null) {
    return null;
  }
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 1) {
    return null;
  }
  return Math.trunc(n);
}

export type ExploreUserEventTimeUpdate = {
  date: string | null;
  focus_time: string | null;
  start_time: string | null;
  end_time: string | null;
};

export type BuildExploreUserEventUpdateResult = {
  times: ExploreUserEventTimeUpdate;
  tagsPatch: Record<string, unknown>;
};

export function buildExploreUserEventUpdateFromForm(
  row: ExploreUserEventRow,
  form: {
    focusLocal: string;
    startLocal: string;
    endLocal: string;
    raceNumberLocal: string;
  }
): BuildExploreUserEventUpdateResult | { error: string } {
  const et = row.event_type.trim().toUpperCase();
  const tagsPatch: Record<string, unknown> = {};

  if (taggerEventTypeIsPrestart(et)) {
    const raceNum = parseRaceNumberInput(form.raceNumberLocal);
    if (raceNum == null) {
      return { error: 'Please enter a valid prestart number (integer ≥ 1).' };
    }
    tagsPatch.Prestart_number = raceNum;
    const iso = datetimeLocalValueToIso(form.focusLocal.trim());
    const base = iso && !Number.isNaN(new Date(iso).getTime()) ? new Date(iso) : new Date();
    const times = timestampsPrestartFromFocusTime(base);
    return {
      times: {
        date: times.date,
        focus_time: times.focus_time,
        start_time: times.start_time,
        end_time: times.end_time,
      },
      tagsPatch,
    };
  }

  if (taggerEventTypeUsesFocusTime(et)) {
    const iso = datetimeLocalValueToIso(form.focusLocal.trim());
    if (!iso) {
      return { error: 'Please enter a valid focus time.' };
    }
    const base = new Date(iso);
    if (Number.isNaN(base.getTime())) {
      return { error: 'Please enter a valid focus time.' };
    }
    const times = timestampsPointWindowFromPress(base);
    return {
      times: {
        date: times.date,
        focus_time: times.focus_time,
        start_time: times.start_time,
        end_time: times.end_time,
      },
      tagsPatch,
    };
  }

  if (et === 'TEST' || et === 'SELECTION' || et === 'SAILS' || et === 'CREW' || et === 'CONFIG' || taggerEventTypeIsRace(et)) {
    const startIso =
      datetimeLocalValueToIso(form.startLocal.trim()) ??
      (row.start_time?.trim() ? row.start_time : null);
    if (!startIso) {
      return { error: 'Please enter a valid start time.' };
    }
    const d = new Date(startIso);
    if (Number.isNaN(d.getTime())) {
      return { error: 'Please enter a valid start time.' };
    }
    const date = localDateYmd(d);
    const endTrim = form.endLocal.trim();
    let endIso: string | null;
    if (endTrim === '') {
      endIso = null;
    } else {
      endIso = datetimeLocalValueToIso(endTrim);
    }
    if (taggerEventTypeIsRace(et)) {
      const rnRaw = form.raceNumberLocal.trim();
      if (rnRaw !== '') {
        const rn = parseRaceNumberInput(form.raceNumberLocal);
        if (rn == null) {
          return { error: 'Race number must be a whole number ≥ 1, or leave blank.' };
        }
        tagsPatch.Race_number = rn;
      } else {
        tagsPatch.Race_number = null;
      }
    }
    return {
      times: {
        date,
        focus_time: startIso,
        start_time: startIso,
        end_time: endIso,
      },
      tagsPatch,
    };
  }

  return {
    times: {
      date: row.date ?? null,
      focus_time: row.focus_time ?? null,
      start_time: row.start_time ?? null,
      end_time: row.end_time ?? null,
    },
    tagsPatch,
  };
}

/** Race number on a RACE or PRESTART tag. Ignores blanks and numbers below 1. */
export function raceTagNumber(
  tags: Record<string, unknown> | null | undefined,
  eventType: string
): number | null {
  if (tags == null) {return null;}
  const keys = ['Race_number', 'race_number', 'Prestart_number', 'prestart_number'];
  if (eventType.trim().toUpperCase() === 'RACE') {
    keys.splice(2);
  }
  for (const key of keys) {
    const raw = tags[key];
    if (raw == null || raw === '') {continue;}
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 1) {return Math.trunc(n);}
  }
  return null;
}

/**
 * Move an interval so it starts at `newStartIso`, keeping the same length and the same
 * offset from start to focus. A missing end stays missing.
 */
export function movedIntervalTimes(
  previousStart: string | null | undefined,
  previousEnd: string | null | undefined,
  previousFocus: string | null | undefined,
  newStartIso: string
): { start_time: string; end_time: string | null; focus_time: string; date: string } | null {
  const newStartMs = new Date(newStartIso).getTime();
  if (!Number.isFinite(newStartMs)) {return null;}
  const oldStartMs = previousStart ? new Date(previousStart).getTime() : NaN;
  const delta = Number.isFinite(oldStartMs) ? newStartMs - oldStartMs : 0;
  let end: string | null = null;
  if (previousEnd && previousEnd.trim() !== '') {
    const oldEndMs = new Date(previousEnd).getTime();
    if (Number.isFinite(oldEndMs)) {
      end = new Date(oldEndMs + delta).toISOString();
    }
  }
  let focus = newStartIso;
  if (previousFocus && previousFocus.trim() !== '') {
    const oldFocusMs = new Date(previousFocus).getTime();
    if (Number.isFinite(oldFocusMs)) {
      focus = new Date(oldFocusMs + delta).toISOString();
    }
  }
  return {
    start_time: new Date(newStartMs).toISOString(),
    end_time: end,
    focus_time: focus,
    date: localDateYmd(new Date(newStartMs)),
  };
}
